"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Cookie, X } from "lucide-react";
import { isSeaaiiHost } from "@/lib/brand";

const STORAGE_KEY = "flowcloser:cookie-consent";

type Choice = "accepted" | "essential";

interface StoredConsent {
  choice: Choice;
  at: string; // ISO timestamp
  version: number;
}

// Bump version when wording materially changes — old consents will be re-prompted
const CONSENT_VERSION = 1;

export default function CookieConsent() {
  // start hidden; reveal only after we've checked localStorage to avoid SSR flash
  const [show, setShow] = useState(false);
  const [details, setDetails] = useState(false);
  // Brand name follows the host so the banner reads "Seaaii" on seaaii.com.
  // Resolved client-side (banner only renders post-mount, so no SSR mismatch).
  const [brandName, setBrandName] = useState("FlowCloser");

  useEffect(() => {
    setBrandName(isSeaaiiHost(window.location.host) ? "Seaaii" : "FlowCloser");
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        setShow(true);
        return;
      }
      const parsed = JSON.parse(raw) as StoredConsent;
      if (!parsed.choice || parsed.version !== CONSENT_VERSION) {
        setShow(true);
      }
    } catch {
      setShow(true);
    }
  }, []);

  const persist = (choice: Choice) => {
    try {
      const payload: StoredConsent = {
        choice,
        at: new Date().toISOString(),
        version: CONSENT_VERSION,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // ignore storage errors — banner will simply re-appear next visit
    }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50"
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      style={{
        background: "#fff",
        borderTop: "1px solid var(--line)",
        boxShadow: "0 -10px 30px -12px rgba(11,20,55,.18)",
      }}
    >
      <div className="mx-auto max-w-5xl px-4 sm:px-6 py-4 sm:py-5">
        <div className="flex items-start gap-4">
            <div
              className="hidden sm:flex w-10 h-10 rounded-xl items-center justify-center flex-shrink-0"
              style={{ background: "var(--bg-soft)", color: "var(--blue-700)", border: "1px solid var(--line)" }}
            >
              <Cookie size={18} />
            </div>

            <div className="flex-1 min-w-0">
              <h3 className="text-[15px] font-semibold mb-1.5" style={{ color: "var(--ink)" }}>
                เราใช้คุกกี้เพื่อให้บริการที่ดีที่สุด
              </h3>
              <p className="text-[13px] leading-relaxed" style={{ color: "var(--ink-2)" }}>
                {brandName} ใช้คุกกี้ที่จำเป็นสำหรับการเข้าสู่ระบบและการรักษาความปลอดภัย
                และคุกกี้เสริมสำหรับจดจำการตั้งค่าของคุณ
                อ่านรายละเอียดเพิ่มเติมที่{" "}
                <Link
                  href="/terms"
                  className="hover:underline font-medium"
                  style={{ color: "var(--blue-700)" }}
                >
                  นโยบายความเป็นส่วนตัว
                </Link>
              </p>

              {details && (
                <div
                  className="mt-3 rounded-xl p-3 text-[12px] leading-relaxed space-y-2"
                  style={{ background: "var(--bg-soft)", border: "1px solid var(--line)", color: "var(--ink-2)" }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider"
                      style={{ background: "#dcfce7", color: "#166534" }}
                    >
                      จำเป็น
                    </span>
                    <span>
                      เซสชันการเข้าสู่ระบบ (NextAuth) · CSRF · ความปลอดภัย — ปิดไม่ได้ ระบบจำเป็นต้องใช้
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span
                      className="mt-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider"
                      style={{ background: "#dbeafe", color: "#1e40af" }}
                    >
                      เสริม
                    </span>
                    <span>
                      จดจำร้านที่เลือกและการตั้งค่าใน UI — ปิดได้ ระบบยังใช้งานได้ปกติแต่ต้องเลือกใหม่ทุกครั้ง
                    </span>
                  </div>
                  <div className="text-[11px] pt-1" style={{ color: "var(--ink-3)" }}>
                    เราไม่ใช้คุกกี้สำหรับโฆษณาหรือ third-party tracking
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 mt-4">
                <button
                  onClick={() => persist("accepted")}
                  className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-all"
                  style={{
                    background: "var(--blue-700)",
                    color: "#fff",
                    boxShadow: "0 1px 0 rgba(255,255,255,.2) inset, 0 8px 22px -8px rgba(37,71,196,.55)",
                  }}
                >
                  ยอมรับทั้งหมด
                </button>
                <button
                  onClick={() => persist("essential")}
                  className="px-4 py-2 rounded-xl text-[13px] font-semibold transition-all"
                  style={{
                    background: "#fff",
                    color: "var(--ink)",
                    border: "1px solid var(--line)",
                  }}
                >
                  เฉพาะที่จำเป็น
                </button>
                <button
                  onClick={() => setDetails((d) => !d)}
                  className="px-3 py-2 rounded-xl text-[12px] font-medium hover:underline ml-1"
                  style={{ color: "var(--ink-3)" }}
                >
                  {details ? "ซ่อนรายละเอียด" : "ดูรายละเอียด"}
                </button>
              </div>
            </div>

          <button
            onClick={() => persist("essential")}
            aria-label="ปิด"
            className="p-1 rounded-md hover:bg-slate-100 transition-colors flex-shrink-0"
            style={{ color: "var(--ink-3)" }}
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
